import { Breadcrumbs, Button, Header, Workspace } from "components/Shared";
import React, { useEffect, useState } from "react";
import { CheckBox, FieldHint } from "../shared";
import axios from "v2/utils/axios";
import { getAuthTokenFromCookie } from "lib/hooks";
import { LocalNotification } from "lib";
import { useCurrentUserInfo } from "routes/hooks";
import SmtpConfiguration from "./SmtpConfiguration";
import UserControls from "./UserControls";

const SettingsPage: React.FC = () => {
  const [currentUser] = useCurrentUserInfo();
  const [ssoEnabled, setSsoEnabled] = useState(currentUser?.sso_enabled || false);
  const currentProtocol = currentUser && (currentUser as any).sso_procotol ? (currentUser as any).sso_procotol : "";
  const [ssoProtocol, setSsoProtocol] = useState("");
  const [ssoConfiguration, setSsoConfiguration] = useState<Record<string, any> | null>(null);
  const [emailDomain, setEmailDomain] = useState("");
  const [smtpFromAddress, setSmtpFromAddress] = useState("");
  // "Save Settings" previously only wrote sso_enabled. Now that it also writes
  // smtp_from_address, it must not send an empty value it never managed to read —
  // a failed or still-in-flight company GET would otherwise wipe a saved address.
  const [companyLoaded, setCompanyLoaded] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [metaDataUrl, setMetadataUrl] = useState("");
  const [showCreateFields, setShowCreateFields] = useState(false);
  // Shows "Copied" on the button for a moment after a successful copy. The
  // Admin Consent URL is long enough that it is cut off in its own field, so
  // selecting it by hand is fiddly and error-prone.
  const [consentUrlCopied, setConsentUrlCopied] = useState(false);

  const adminConsentUrl = ssoConfiguration ? ssoConfiguration.data.attributes.admin_consent_url : "";

  const copyAdminConsentUrl = async () => {
    if (!adminConsentUrl) return;
    try {
      // navigator.clipboard needs a secure context; fall back to the old
      // execCommand path so this still works over plain http in development.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(adminConsentUrl);
      } else {
        const scratch = document.createElement("textarea");
        scratch.value = adminConsentUrl;
        scratch.setAttribute("readonly", "");
        scratch.style.position = "fixed";
        scratch.style.opacity = "0";
        document.body.appendChild(scratch);
        scratch.select();
        document.execCommand("copy");
        document.body.removeChild(scratch);
      }
      setConsentUrlCopied(true);
      setTimeout(() => setConsentUrlCopied(false), 2000);
    } catch (err) {
      console.error("Error copying the admin consent URL:", err);
      new LocalNotification("Could not copy the URL", "Could not copy the URL", "error");
    }
  };

  const handleChange = (selectedOption: any) => {
    setSsoProtocol(selectedOption.target.value);
    setShowCreateFields(true);
  }

  useEffect(() => {
    if(currentUser) {
      setSsoEnabled(currentUser.sso_enabled);

      // Company settings. sso_enabled is already on currentUser, but
      // smtp_from_address is only exposed on the company record.
      axios.get(`${process.env.REACT_APP_API_ENDPOINT_V2}admin/companies/${(currentUser as any).company_id}`, {
        headers: {
          Authorization: `Bearer ${getAuthTokenFromCookie()}`,
        }
      })
      .then((res) => {
        setSmtpFromAddress(res.data.data.attributes.smtp_from_address || "");
        setCompanyLoaded(true);
      })
      .catch(err => {
        console.error("Error fetching company settings:", err);
      });

      // Fetch SSO configuration based on selected protocol
      axios.get(`${process.env.REACT_APP_API_ENDPOINT_V2}admin/companies/${(currentUser as any).company_id}/sso`, {
        headers: {
          Authorization: `Bearer ${getAuthTokenFromCookie()}`,
        }
      })
      .then((res) => {
        setSsoConfiguration(res.data);
        setEmailDomain(res.data.data.attributes.email_domain || "");
        setTenantId(res.data.data.attributes.configuration.tenant_id || "");
        setMetadataUrl(res.data.data.attributes.configuration.metadata_url || "");
      })
      .catch(err => {
        console.error("Error fetching SSO configuration:", err);
      });
    }
  }, [currentUser]);

  const handleSaveSettings = () => {
    const companyPayload: Record<string, any> = { sso_enabled: ssoEnabled };
    if (companyLoaded) companyPayload.smtp_from_address = smtpFromAddress;

    axios.put(`${process.env.REACT_APP_API_ENDPOINT_V2}admin/companies/${(currentUser as any).company_id}`, { "company": companyPayload }, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthTokenFromCookie()}`,
      }
    })
    .then((res) => {
      new LocalNotification("Company settings saved!");
    })
    .catch(err => {
      const detail = err?.response?.data?.errors?.detail;
      const message = Array.isArray(detail) ? detail.join(", ") : detail || "Something Went Wrong";
      new LocalNotification(message, message, "error");
      console.error("Error saving settings:", err);
    });
  };

  const handleCreateSsoConfiguration = () => {
    const config = ssoProtocol === 'oidc' ? { tenant_id: tenantId } : { metadata_url: metaDataUrl };
    axios.post(`${process.env.REACT_APP_API_ENDPOINT_V2}admin/companies/${(currentUser as any).company_id}/sso`, { 
      "sso": { 
        protocol: ssoProtocol,
        idp: 'microsoft',
        email_domain: emailDomain,
        configuration: config
      }
    }, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthTokenFromCookie()}`,
      }
    })
    .then((res) => {
      new LocalNotification("SSO Configuration Created!");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    })
    .catch(err => {
      new LocalNotification("Something Went Wrong", "Something Went Wrong", "error");
      console.error("Error saving settings:", err);
    });
  };

  const handleUpdateSsoConfiguration = () => {
    const config = currentProtocol === 'Oidc' ? { tenant_id: tenantId } : { metadata_url: metaDataUrl };
    axios.put(`${process.env.REACT_APP_API_ENDPOINT_V2}admin/companies/${(currentUser as any).company_id}/sso`, { 
      "sso": { 
        idp: 'microsoft',
        email_domain: emailDomain,
        configuration: config
      }
    }, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthTokenFromCookie()}`,
      }
    })
    .then((res) => {
      new LocalNotification("SSO Configuration Updated!");
    })
    .catch(err => {
      new LocalNotification("Something Went Wrong", "Something Went Wrong", "error");
      console.error("Error saving settings:", err);
    });
  };

  return (
    <React.Fragment>
      <Breadcrumbs items={[{ title: "settings" }]} />
      {/* The page is one company's settings, so the heading says so; the section
          below it is the general ones, as opposed to SSO, Users and SMTP. */}
      <Header title="Company Settings" stamp="settings" />
      <Workspace id="settings" className="d-flex flex-column gap-4">
        <div className="px-5 py-4 bg-white">
          <div className="gap-3 d-flex align-items-center mb-4">
            <h2 className="text-24 font-bold mb-0">General Settings</h2>
            <p className="text-14">Manage your company settings and preferences.</p>
          </div>
          <div className="mb-3">
            <CheckBox checked={ssoEnabled} onChange={() => setSsoEnabled(!ssoEnabled)} />
            <span className="ml-2 text-16">Enable Single Sign-On (SSO)</span>
          </div>

          <div className="d-flex align-items-center mb-1">
            <label htmlFor="smtpFromAddress" className="mb-0">SMTP from address</label>
            <FieldHint text="Email address that notification email about your properties is sent from. The sender name is always 'TCPinpoint', so this address becomes TCPinpoint <address>. A property's own address takes precedence. Only used when your company has an SMTP configuration below — without one, email is sent as the TCPinpoint default." />
          </div>
          <input
            id="smtpFromAddress"
            className="form-control"
            type="text"
            placeholder="e.g. notifications@example.com.au"
            value={smtpFromAddress}
            disabled={!companyLoaded}
            onChange={(e: any) => setSmtpFromAddress(e.target.value)}
          />

          <div className="mt-3 d-flex justify-content-end">
            <Button onClick={handleSaveSettings}>
              Save Settings
            </Button>
          </div>
        </div>

        <div className="px-5 py-4 bg-white">
          {ssoConfiguration ? (
            <>
              <div className="gap-3 d-flex align-items-center mb-4">
                <h2 className="text-24 font-bold mb-0">SSO Configuration</h2>
                <p className="text-14">Manage your Single Sign-On (SSO) configuration.</p>
              </div>
              <label htmlFor="protocol">Protocol</label>
              <input id="protocol" className="form-control" type="text" value={currentProtocol} disabled />
              <label htmlFor="identityProvider" className="mt-3">Identity Provider</label>
              <input id="identityProvider" className="form-control" type="text" value={ssoConfiguration ? ssoConfiguration.data.type : "" } disabled />
              <label htmlFor="emailDomain" className="mt-3">Email Domain</label>
              <input id="emailDomain" className="form-control" type="text" value={emailDomain} onChange={(e: any) => setEmailDomain(e.target.value)} />
              {
                currentProtocol === 'Oidc' ? (
                  <>      
                    <label htmlFor="tenantId" className="mt-3">Tenant ID</label>
                    <input id="tenantId" className="form-control" type="text" value={tenantId} onChange={(e: any) => setTenantId(e.target.value)}  />
                    <label htmlFor="adminConsentUrl" className="mt-3">Admin Consent URL</label>
                    <div className="input-group">
                      <input id="adminConsentUrl" className="form-control" type="text" value={adminConsentUrl} disabled />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={copyAdminConsentUrl}
                          disabled={!adminConsentUrl}
                          title={consentUrlCopied ? "Copied" : "Copy to clipboard"}
                          aria-label="Copy the admin consent URL to the clipboard"
                        >
                          <i className={consentUrlCopied ? "far fa-check" : "far fa-copy"} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <label htmlFor="metadataUrl" className="mt-3">Metadata URL</label>
                    <input id="metadataUrl" className="form-control" type="text" value={metaDataUrl} onChange={(e: any) => setMetadataUrl(e.target.value)}  />
                    <label htmlFor="EntityId" className="mt-3">Identifier (Entity ID)</label>
                    <input id="entityId" className="form-control" type="text" value={ssoConfiguration ? ssoConfiguration.data.attributes.issuer_url : ""} disabled  />
                    <label htmlFor="metadataUrl" className="mt-3">Assertion Consumer Service URL</label>
                    <input id="metadataUrl" className="form-control" type="text" value={ssoConfiguration ? ssoConfiguration.data.attributes.acs_url : ""} disabled  />
                  </>
                )
              }
              
              <div className="mt-3 d-flex justify-content-end">
                <Button onClick={handleUpdateSsoConfiguration}>
                  Update SSO Configuration
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="gap-3 d-flex align-items-center mb-4">
                <h2 className="text-24 font-bold mb-0">SSO Configuration</h2>
                <p className="text-14">Manage your Single Sign-On (SSO) configuration.</p>
              </div>
              <label htmlFor="protocol">Protocol</label>
              <select id="protocol" className="form-control" value={ssoProtocol} onChange={handleChange}>
                <option value="">Select a Protocol</option>
                <option value="oidc">OIDC</option>
                <option value="saml">SAML</option>
              </select>
              {
                showCreateFields && (
                  <>
                    <label htmlFor="emailDomain" className="mt-3">Email Domain</label>
                    <input id="emailDomain" className="form-control" type="text" value={emailDomain} onChange={(e: any) => setEmailDomain(e.target.value)} />
                    {
                      ssoProtocol === 'oidc' ? (
                        <>      
                          <label htmlFor="tenantId" className="mt-3">Tenant ID</label>
                          <input id="tenantId" className="form-control" type="text" value={tenantId} onChange={(e: any) => setTenantId(e.target.value)}  />
                        </>
                      ) : (
                        <>
                          <label htmlFor="metadataUrl" className="mt-3">Metadata URL</label>
                          <textarea id="metadataUrl" className="form-control" value={metaDataUrl} onChange={(e: any) => setMetadataUrl(e.target.value)}  />
                          <label htmlFor="entity_id" className="mt-3">Identifier (Entity ID)</label>
                          <input id="entity_id" className="form-control" type="text" value={`${process.env.REACT_APP_API_ENDPOINT_V1}sso/microsoft/saml`} disabled  />
                          <label htmlFor="acs_url" className="mt-3">Assertion Consumer Service URL</label>
                          <input id="acs_url" className="form-control" type="text" value={`${process.env.REACT_APP_API_ENDPOINT_V1}sso/microsoft/saml/${(currentUser as any).company_id}`} disabled  />
                        </>
                      )
                    }
                  </>
                )
              }
              <div className="mt-3 d-flex justify-content-end">
                <Button onClick={handleCreateSsoConfiguration}>
                  Create SSO Configuration
                </Button>
              </div>
            </>
          )}
          
        </div>

        {/* The gate is load-bearing, not just cosmetic: both sections call
            /v2/admin endpoints that 401 a non-admin, and that 401 goes through
            the axios refresh interceptor and bounces to /signin. */}
        {!!(currentUser as any)?.app_admin && (
          <>
            <UserControls />
            <SmtpConfiguration companyId={(currentUser as any).company_id} />
          </>
        )}
      </Workspace>
    </React.Fragment>
  );
};

export default SettingsPage;